import { partnerStrings } from '@data/partners';
import { PaymentSlipPaymentType } from '@models/next/PaymentSlip';
import { z } from 'zod';

export const PartnerContact = z.object({
  name: z.string().label(partnerStrings.contactName),
  email: z.string().email().label(partnerStrings.email),
  phone: z.string().label(partnerStrings.phone),
});

export type PartnerContact = z.infer<typeof PartnerContact>;

export const PartnerType = z.enum(['通関業者', '運送業者', '倉庫業者', '税関検査業者', 'その他']);

export type PartnerType = z.infer<typeof PartnerType>;

export const CutoffDateMonthType = z.enum(['請求毎', '当月']);

export type CutoffDateMonthType = z.infer<typeof CutoffDateMonthType>;

export const CutoffDateDayType = z.enum(['請求', '日締', '月末締', '10日毎', '15日毎']);

export type CutoffDateDayType = z.infer<typeof CutoffDateDayType>;

export const PaymentDateMonthType = z.enum(['請求毎', '当月', '翌月', '翌々']);

export type PaymentDateMonthType = z.infer<typeof PaymentDateMonthType>;

export const PaymentDateDayType = z.enum(['請求', '日払', '月末払', '10日毎', '15日毎']);

export type PaymentDateDayType = z.infer<typeof PaymentDateDayType>;

export const PartnerPaymentCondition = z.object({
  cutoff_date_month: CutoffDateMonthType,
  cutoff_date_selected_day: z.number().nullish(),
  cutoff_date_day: CutoffDateDayType,
  payment_date_month: PaymentDateMonthType,
  payment_date_selected_day: z.number().nullish(),
  payment_date_day: PaymentDateDayType,
});

export type PartnerPaymentCondition = z.infer<typeof PartnerPaymentCondition>;

export const Partner = z.object({
  id: z.number(),
  fc_no: z.string().label(partnerStrings.fcNo),
  name_en: z.string().label(partnerStrings.name_en).nullish(),
  name_ja: z.string().label(partnerStrings.name_ja),
  name_en_short: z.string().label(partnerStrings.short_name_en),
  type: PartnerType.array().label(partnerStrings.type),
  type_description: z.string().label(partnerStrings.type_description).nullish(),
  tel: z.string().label(partnerStrings.tel),
  fax: z.string().label(partnerStrings.fax).nullish(),
  address: z.string().label(partnerStrings.address).nullish(),
  registration_number: z.string().label(partnerStrings.registrationNumber).nullish(),
  corporate_number: z.string().label(partnerStrings.corporateNumber).nullish(),
  contacts: z.array(PartnerContact).label(partnerStrings.contactAndEmail),
  payment_conditions: z.object({
    [PaymentSlipPaymentType.Enum.現払い]: PartnerPaymentCondition,
    [PaymentSlipPaymentType.Enum.締払い]: PartnerPaymentCondition,
  }),
  default_payment_details: z.object({
    [PaymentSlipPaymentType.Enum.現払い]: z.number().array(),
    [PaymentSlipPaymentType.Enum.締払い]: z.number().array(),
  }),
  memo1: z.string().label(partnerStrings.memo1).nullish(),
  memo2: z.string().label(partnerStrings.memo2).nullish(),
});

export type Partner = z.infer<typeof Partner>;

export const PartnerQueryType = z.object({
  type: PartnerType.optional(),
});

export type PartnerQueryType = z.infer<typeof PartnerQueryType>;

export const PostPartnerContact = z.object({
  name: PartnerContact.shape.name.min(1),
  email: PartnerContact.shape.email.min(1),
  phone: PartnerContact.shape.phone,
});

export type PostPartnerContact = z.infer<typeof PartnerContact>;

export const PostPartner = z.object({
  fc_no: Partner.shape.fc_no.min(1),
  name_en: Partner.shape.name_en,
  name_ja: Partner.shape.name_ja.min(1),
  name_en_short: Partner.shape.name_en_short.min(1),
  type: Partner.shape.type.min(1),
  type_description: Partner.shape.type_description,
  tel: Partner.shape.tel.min(1),
  fax: Partner.shape.fax,
  address: Partner.shape.address,
  registration_number: Partner.shape.registration_number,
  corporate_number: Partner.shape.corporate_number,
  contacts: PostPartnerContact.array().min(1).label(partnerStrings.contactAndEmail),
  payment_conditions: z
    .object({
      現払い: PartnerPaymentCondition,
      締払い: PartnerPaymentCondition,
    })
    .label(partnerStrings.paymentConditions),
  memo1: Partner.shape.memo1,
  memo2: Partner.shape.memo2,
  default_payment_details: Partner.shape.default_payment_details,
});

export type PostPartner = z.infer<typeof PostPartner>;

export const PatchPartner = PostPartner.partial();

export type PatchPartner = z.infer<typeof PatchPartner>;

export const PartnerForm = PostPartner.superRefine((v, ctx) => {
  if (
    v.payment_conditions.現払い.cutoff_date_day === '日締' &&
    !v.payment_conditions.現払い.cutoff_date_selected_day
  ) {
    ctx.addIssue({
      code: 'custom',
      message: partnerStrings.errorNoSelectedDate,
      path: ['payment_conditions', '現払い', 'cutoff_date_selected_day'],
    });
  }

  if (
    v.payment_conditions.現払い.payment_date_day === '日払' &&
    !v.payment_conditions.現払い.payment_date_selected_day
  ) {
    ctx.addIssue({
      code: 'custom',
      message: partnerStrings.errorNoSelectedDate,
      path: ['payment_conditions', '現払い', 'payment_date_selected_day'],
    });
  }

  if (
    v.payment_conditions.締払い.cutoff_date_day === '日締' &&
    !v.payment_conditions.締払い.cutoff_date_selected_day
  ) {
    ctx.addIssue({
      code: 'custom',
      message: partnerStrings.errorNoSelectedDate,
      path: ['payment_conditions', '締払い', 'cutoff_date_selected_day'],
    });
  }

  if (
    v.payment_conditions.締払い.payment_date_day === '日払' &&
    !v.payment_conditions.締払い.payment_date_selected_day
  ) {
    ctx.addIssue({
      code: 'custom',
      message: partnerStrings.errorNoSelectedDate,
      path: ['payment_conditions', '締払い', 'payment_date_selected_day'],
    });
  }

  if (v.type.includes('その他') && !v.type_description) {
    ctx.addIssue({
      code: 'custom',
      message: partnerStrings.typeDescriptionIsRequired,
      path: ['type_description'],
    });
  }
});

export type PartnerForm = z.infer<typeof PartnerForm>;

export class PartnerHelper {
  static mapToPost(v: Partner) {
    return {
      ...v,
    };
  }
}

export const initialPartner: PostPartner = {
  fc_no: '',
  name_en: '',
  name_ja: '',
  name_en_short: '',
  type: [],
  tel: '',
  fax: '',
  address: '',
  contacts: [
    {
      name: '',
      email: '',
      phone: '',
    },
  ],
  payment_conditions: {
    現払い: {
      cutoff_date_month: '請求毎',
      cutoff_date_selected_day: null,
      cutoff_date_day: '請求',
      payment_date_month: '請求毎',
      payment_date_selected_day: null,
      payment_date_day: '請求',
    },
    締払い: {
      cutoff_date_month: '請求毎',
      cutoff_date_selected_day: null,
      cutoff_date_day: '請求',
      payment_date_month: '請求毎',
      payment_date_selected_day: null,
      payment_date_day: '請求',
    },
  },
  default_payment_details: {
    現払い: [],
    締払い: [],
  },
};

export const examplePartner: Partner = {
  id: 1,
  fc_no: '123456',
  name_en: 'Test Partner',
  name_ja: 'テストパートナー',
  name_en_short: 'TP',
  type: ['通関業者', '税関検査業者'],
  tel: '0123456789',
  fax: '0123456789',
  address: '123-4567 東京都新宿区',
  contacts: [
    {
      name: 'Test Contact',
      email: 'test@email.com',
      phone: '0123456789',
    },
  ],
  payment_conditions: {
    現払い: {
      cutoff_date_month: '請求毎',
      cutoff_date_selected_day: null,
      cutoff_date_day: '請求',
      payment_date_month: '請求毎',
      payment_date_selected_day: null,
      payment_date_day: '請求',
    },
    締払い: {
      cutoff_date_month: '当月',
      cutoff_date_selected_day: 10,
      cutoff_date_day: '10日毎',
      payment_date_month: '翌月',
      payment_date_selected_day: 10,
      payment_date_day: '10日毎',
    },
  },
  default_payment_details: {
    現払い: [],
    締払い: [],
  },
};

export const examplePartners: Partner[] = [
  {
    id: 1,
    fc_no: '123456',
    name_en: 'Test Partner',
    name_ja: 'テストパートナー',
    name_en_short: 'TP',
    type: ['通関業者'],
    tel: '0123456789',
    fax: '0123456789',
    address: '123-4567 東京都新宿区',
    contacts: [
      {
        name: 'Test Contact',
        email: 'test@email.com',
        phone: '0123456789',
      },
    ],
    payment_conditions: {
      現払い: {
        cutoff_date_month: '請求毎',
        cutoff_date_selected_day: null,
        cutoff_date_day: '請求',
        payment_date_month: '請求毎',
        payment_date_selected_day: null,
        payment_date_day: '請求',
      },
      締払い: {
        cutoff_date_month: '当月',
        cutoff_date_selected_day: 10,
        cutoff_date_day: '10日毎',
        payment_date_month: '翌月',
        payment_date_selected_day: 10,
        payment_date_day: '10日毎',
      },
    },
    default_payment_details: {
      現払い: [],
      締払い: [],
    },
  },
];
